import { SENDA } from '@somnhold/content';
import { cellId } from '../grid.ts';
import { hexToWorld } from '../hex.ts';
import { idiv, imax } from '../math/fixed.ts';
import type { GargRules, KneesRules, PlatesRules, ShellRules, TornoRules } from '../types.ts';
import { platesOrder, type KneesLayout, type PlatesLayout, type ShellLayout, type TornoLayout } from './tormo-layout.ts';

/**
 * Tormo's rules from their layouts (docs/sendas/artifice.md, chapter 5): the fight's builder (campaign/sendas.ts) and the
 * renderer's scenes build them the same way. SENDA.knees, torno, shell and plates. Lives and blows in hit points (seconds
 * of D, worked out by the caller). `radius`: the board's, for cell ids.
 */

export const kneesRules = (L: KneesLayout): KneesRules => {
  const K = SENDA.knees;
  return {
    way: L.way.map((p) => ({ ...p })),
    len: [...L.len],
    total: L.total,
    joints: L.joints.map((j) => ({ off: { ...j.off }, gears: j.gears, drive: j.drive, free: j.free.map((f) => ({ ...f })), jam: j.jam })),
    holes: L.holes.map((p) => ({ ...p })),
    stand: K.stand,
    reach: K.reach,
    gap: K.gap,
    grab: K.grab,
    people: K.people,
    tormo: K.tormo,
    lockSlow: K.lockSlow,
    lunge: K.lunge,
    piles: L.piles.map((p) => ({ ...p })),
    take: K.take,
    carryBp: K.carryBp,
    stomp: { ...K.stomp },
    quake: { ...K.quake },
    debris: { ...K.debris },
    foes: { ...K.foes },
    fall: K.fall,
  };
};

export const tornoRules = (L: TornoLayout, radius: number, gargHp: number, crowHp: number, dunnaHit: number): { torno: TornoRules; garg: GargRules } => {
  const T = SENDA.torno;
  const G = T.garg;
  const id = (h: { q: number; r: number }): number => cellId(radius, h.q, h.r);
  return {
    torno: {
      slots: L.slots.map(id),
      lie: L.lie.map(id),
      lever: id(L.lever),
      pieces: [...L.set.pieces],
      input: T.input,
      wantNum: L.set.wantNum,
      wantDen: L.set.wantDen,
      stand: T.stand,
      pull: T.pull,
      reach: T.reach,
      carryBp: [...T.carryBp],
      spans: T.spans,
      climb: T.climb,
      haul: T.haul,
      slipBlows: T.slipBlows,
      cable: T.cable,
      peck: T.peck,
      flock: { ...T.flock },
      rocks: { ...T.rocks },
      shower: { ...T.shower },
      drum: { ...L.drum },
      top: { ...L.top },
      dunnaAt: { ...L.dunnaAt },
      dunna: { every: T.dunna.every, hit: dunnaHit, range: T.dunna.range },
    },
    garg: {
      hp: gargHp,
      crowHp,
      pace: G.pace,
      reach: G.reach,
      reload: G.reload,
      heroBp: G.heroBp,
      blows: G.blows,
      peckR: G.peckR,
      perches: L.perches.map((p) => ({ x: p.x, y: p.y, kind: 2, slot: -1 })),
      from: { ...L.from },
    },
  };
};

export const shellRules = (L: ShellLayout, radius: number, dunnaHit: number): ShellRules => {
  const S = SENDA.shell;
  const id = (h: { q: number; r: number }): number => cellId(radius, h.q, h.r);
  return {
    drills: L.drills.map((h, d) => ({ cell: id(h), spot: id(L.spots[d] as { q: number; r: number }), at: hexToWorld(h.q, h.r), force: L.set.force[d] as number, right0: L.set.right0[d] as boolean })),
    gang: L.set.gang,
    chestAt: hexToWorld(L.chest.q, L.chest.r),
    chestForce: S.chestForce,
    reach: L.set.reach.map((x) => [...x]),
    need: [...L.set.need],
    stones: L.stones.map((p) => ({ ...p })),
    tormoAt: { ...L.tormoAt },
    levers: L.levers.map(id),
    holes: L.holes.map((p) => ({ ...p })),
    mill: S.mill,
    clutch: S.clutch,
    lever: S.lever,
    near: S.reach,
    cut: S.cut,
    cracks: S.cracks,
    jamBp: S.jamBp,
    twist: { ...S.twist },
    shards: { ...S.shards },
    cubs: { ...S.cubs },
    dunnaAt: { ...L.dunnaAt },
    dunna: { every: S.dunna.every, hit: dunnaHit, range: S.dunna.range },
  };
};

/**
 * Tormo's fight: six plates (seven with the old dust; `scars` of them dark), the nightmare's life `hp` over them, his iron
 * `iron`, the crossbows' bolt, the picks' and Dunna's; Grillo's dust moves the glow faster.
 */
export const platesRules = (
  L: PlatesLayout,
  seed: number,
  o: {
    old: boolean;
    scars: number;
    hp: number;
    iron: number;
    bolt: number;
    pick: number;
    dunna: number;
    cub: number;
  },
): PlatesRules => {
  const P = SENDA.plates;
  const n = o.old ? 7 : 6;
  return {
    order: platesOrder(seed, n),
    dark: o.scars,
    per: imax(1, idiv(o.hp, n)),
    iron: o.iron,
    side: P.side,
    bows: L.bows.map((p) => ({ ...p })),
    every: P.every,
    bolt: o.bolt,
    jam: P.jam,
    tormoAt: { ...L.tormoAt },
    naddAt: { ...L.naddAt },
    pace: P.pace,
    turn: P.turn,
    fist: { ...P.fist },
    stamp: { ...P.stamp },
    rocks: { ...P.rocks },
    cubs: { n: P.cubs.n, hp: o.cub },
    move: o.old ? P.move : P.grilloMove,
    hammer: { ...P.hammer },
    dust: { ...P.dust },
    softAt: P.softAt,
    softFast: P.softFast,
    soft: { ...P.soft },
    pick: { every: P.pick.every, hit: o.pick, range: P.pick.range },
    dunna: { every: P.dunna.every, hit: o.dunna, range: P.dunna.range },
    end: P.end,
  };
};
